// frontend/src/modules/expenses/pages/ExpenseEditorPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, X, ArrowLeft, FileText } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { usePermission } from '../../../core/auth/usePermission';
import { VISUAL } from '../../../design-system/visual';
import { CategoryPicker } from '../components/CategoryPicker';
import { AmountInput } from '../components/AmountInput';
import { ReceiptUpload, type PendingFile } from '../components/ReceiptUpload';
import { formatMoney } from '../../../design-system/format';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { expensesApi } from '../api';
import type { Expense, Category, ApprovalChain, CreateExpenseInput, UpdateExpenseInput } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

interface FormState {
  category_id: string;
  amount: number | '';
  currency: string;
  spent_at: string;
  description: string;
  quantity: number | '';
  reimbursable: boolean;
  approval_chain_id: string; // 'none' = no chain
}

export function ExpenseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mode: 'edit' | 'new' = id ? 'edit' : 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency ?? 'USD';

  const { data, loading } = useApi<{ expense: Expense }>(
    mode === 'edit' && id ? `/api/expenses/${id}` : null,
  );

  const [form, setForm] = React.useState<FormState>({
    category_id: '',
    amount: '',
    currency: tenantCurrency,
    spent_at: todayISO(),
    description: '',
    quantity: '',
    reimbursable: false,
    approval_chain_id: 'none',
  });
  const [selectedCategory, setSelectedCategory] = React.useState<Category | null>(null);
  const [pending, setPending] = React.useState<PendingFile[]>([]);
  const [existing, setExisting] = React.useState<Expense['attachments']>([]);
  const [saving, setSaving] = React.useState(false);

  // Approval chains for the optional routing picker (new expenses only).
  const chainsQ = useApi<{ rows: ApprovalChain[] }>(mode === 'new' ? '/api/expenses/approval-chains' : null);
  const chains = chainsQ.data?.rows ?? [];

  // "On behalf of" picker for approvers/managers (backend already accepts
  // employee_id on create; server-side permission gates remain the enforcement
  // — this only decides whether to render the control).
  const canOnBehalf = usePermission('expenses.approve');
  const [onBehalfEmployeeId, setOnBehalfEmployeeId] = React.useState<string>('self');
  const employeesQ = useApi<{ employees: EmployeeWithUser[] }>(
    mode === 'new' && canOnBehalf ? '/api/hr/employees' : null,
  );
  const employees = employeesQ.data?.employees ?? [];

  const isRateBased = selectedCategory != null && selectedCategory.kind !== 'general';
  const rate = selectedCategory?.rate != null ? Number(selectedCategory.rate) : 0;
  const computedAmount = isRateBased && form.quantity !== '' ? rate * Number(form.quantity) : 0;

  // Hydrate from loaded expense
  React.useEffect(() => {
    if (mode === 'edit' && data?.expense) {
      const e = data.expense;
      setForm({
        category_id: e.category_id,
        amount: Number(e.amount),
        currency: e.currency,
        spent_at: e.spent_at.slice(0, 10),
        description: e.description ?? '',
        quantity: e.quantity != null ? Number(e.quantity) : '',
        reimbursable: e.reimbursable,
        approval_chain_id: e.approval_chain_id ?? 'none',
      });
      setExisting(e.attachments);
    }
  }, [data, mode]);

  // For new expenses, default the currency to the tenant once branding loads.
  React.useEffect(() => {
    if (mode === 'new' && tenant && !form.currency) {
      setForm((f) => ({ ...f, currency: tenantCurrency }));
    }
  }, [tenant, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(files: File[]) {
    const next: PendingFile[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: 'queued',
    }));
    setPending((p) => [...p, ...next]);
  }

  function removePending(pid: string) {
    setPending((p) => p.filter((x) => x.id !== pid));
  }

  async function removeExistingAttachment(attachmentId: string) {
    if (!id) return;
    if (!(await confirm({
      title: 'Remove this attachment?',
      destructive: true,
      confirmLabel: 'Remove',
    }))) return;
    try {
      await expensesApi.removeAttachment(id, attachmentId);
      setExisting((xs) => xs.filter((a) => a.id !== attachmentId));
      toast.success('Attachment removed');
    } catch (err: any) {
      toast.error('Remove failed', { description: err.message });
    }
  }

  async function uploadPending(expenseId: string): Promise<void> {
    if (pending.length === 0) return;
    // Mark all uploading
    setPending((list) => list.map((p) => ({ ...p, status: 'uploading' as const })));
    for (const p of pending) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await expensesApi.uploadAttachment(expenseId, p.file);
        setPending((list) => list.filter((x) => x.id !== p.id));
      } catch (err: any) {
        setPending((list) =>
          list.map((x) =>
            x.id === p.id ? { ...x, status: 'error' as const, error: err.message ?? 'Upload failed' } : x,
          ),
        );
        // Surface but keep iterating so the user sees a per-file error trail.
        toast.error(`Failed to upload ${p.file.name}`, { description: err.message ?? String(err) });
      }
    }
  }

  async function doSave(asDraft: boolean) {
    if (!form.category_id) { toast.error('Pick a category'); return; }
    if (isRateBased) {
      if (form.quantity === '' || !(Number(form.quantity) > 0)) {
        toast.error(`Enter the number of ${selectedCategory?.unit ?? 'units'} (greater than zero)`);
        return;
      }
    } else if (form.amount === '' || !(Number(form.amount) > 0)) {
      toast.error('Enter an amount greater than zero'); return;
    }
    if (!form.currency) { toast.error('Currency required'); return; }
    if (!form.spent_at) { toast.error('Date required'); return; }

    setSaving(true);
    try {
      if (mode === 'edit' && id) {
        // Edit only mutates the legacy free-amount fields (rate-based amounts are
        // derived server-side and not re-editable here).
        const patch: UpdateExpenseInput = {
          category_id: form.category_id,
          amount: Number(form.amount),
          currency: form.currency,
          spent_at: form.spent_at,
          description: form.description.trim() || null,
        };
        const updated = await expensesApi.update(id, patch);
        await uploadPending(updated.id);
        toast.success('Expense updated');
        navigate(`/dashboard/expenses/${updated.id}`);
      } else {
        const input: CreateExpenseInput = {
          category_id: form.category_id,
          // amount is ignored server-side for rate-based categories; send 0.
          amount: isRateBased ? 0 : Number(form.amount),
          currency: form.currency,
          spent_at: form.spent_at,
          description: form.description.trim() || null,
          quantity: isRateBased ? Number(form.quantity) : null,
          reimbursable: form.reimbursable,
          approval_chain_id: form.approval_chain_id !== 'none' ? form.approval_chain_id : null,
          // Approvers can log on behalf of a team member; everyone else
          // (and 'self') lets the backend resolve their own employee row.
          employee_id: canOnBehalf && onBehalfEmployeeId !== 'self' ? onBehalfEmployeeId : null,
          status: asDraft ? 'draft' : 'submitted',
        };
        const created = await expensesApi.create(input);
        await uploadPending(created.id);
        toast.success(asDraft ? 'Draft saved — submit it when it’s ready' : 'Expense created');
        navigate(`/dashboard/expenses/${created.id}`);
      }
    } catch (err: any) {
      if (err?.code === 'not_employee') {
        // A non-employee (e.g. an owner/admin account) has no employee row to
        // attach the expense to — explain instead of surfacing the raw 404.
        toast.error('Your account isn’t linked to an employee', {
          description: canOnBehalf
            ? 'Use "On behalf of" above to pick the team member this expense is for, or add yourself as an employee in HR first.'
            : 'Ask an admin to add you as an employee in HR before logging expenses.',
        });
      } else {
        toast.error('Save failed', { description: err.message });
      }
    } finally {
      setSaving(false);
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    await doSave(false);
  }

  if (mode === 'edit' && loading) return <LoadingState variant="page" />;
  if (mode === 'edit' && data && data.expense.status !== 'submitted' && data.expense.status !== 'draft') {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-10">
        <p className="text-[14px] text-ink dark:text-white">
          This expense is <strong>{data.expense.status}</strong> and can't be edited.{' '}
          <Link to={`/dashboard/expenses/${id}`} className="underline hover:text-coral-600">
            Back to detail
          </Link>.
        </p>
      </div>
    );
  }

  const cancelHref = mode === 'edit' && id ? `/dashboard/expenses/${id}` : '/dashboard/expenses';

  return (
    <form onSubmit={save} className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / '}
            {mode === 'edit' ? <span className="font-mono">{id?.slice(0, 8)}</span> : 'New'}
          </>
        }
        title={mode === 'edit' ? 'Edit expense' : 'Log an expense'}
        description={
          mode === 'edit'
            ? 'Tweak the amount, dates or attached receipts before resubmitting.'
            : 'Capture a business expense. Attach receipts and we will route it for approval.'
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to={cancelHref}>
              <Button variant="outline" type="button" size="default" className="gap-1.5">
                <X className="h-3.5 w-3.5" /> Cancel
              </Button>
            </Link>
            {mode === 'new' && (
              <Button
                type="button"
                variant="outline"
                size="default"
                disabled={saving}
                className="gap-1.5"
                onClick={() => doSave(true)}
              >
                <FileText className="h-3.5 w-3.5" />
                Save as draft
              </Button>
            )}
            <Button type="submit" disabled={saving} size="default" className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Submit'}
            </Button>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-14 max-w-4xl space-y-6">
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Expense details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {mode === 'new' && canOnBehalf && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>On behalf of</Label>
                <Select value={onBehalfEmployeeId} onValueChange={setOnBehalfEmployeeId}>
                  <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[12px] text-ink/50 dark:text-white/50">
                  Log this expense for a team member — it will appear under their name.
                </p>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Category<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
              <CategoryPicker
                value={form.category_id || null}
                onChange={(cid) => setForm((f) => ({ ...f, category_id: cid }))}
                onCategoryChange={setSelectedCategory}
                disabled={mode === 'edit'}
              />
            </div>

            {isRateBased ? (
              <>
                <div className="space-y-1.5">
                  <Label>
                    Quantity ({selectedCategory?.unit ?? 'units'})
                    <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.001"
                    inputMode="decimal"
                    value={form.quantity === '' ? '' : form.quantity}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setForm((f) => ({ ...f, quantity: raw === '' ? '' : Number(raw) }));
                    }}
                    placeholder={`Number of ${selectedCategory?.unit ?? 'units'}`}
                    className="font-mono tabular-nums text-right"
                    disabled={mode === 'edit'}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Computed amount</Label>
                  <div className="h-10 flex items-center px-3 rounded-xl bg-pearl-100/70 dark:bg-white/[0.05] text-[14px] font-semibold font-mono tabular-nums text-ink dark:text-white">
                    {formatMoney(computedAmount, form.currency || tenantCurrency)}
                    <span className="ml-2 text-[11px] font-normal text-ink/50 dark:text-white/50">
                      {form.quantity || 0} × {formatMoney(rate, form.currency || tenantCurrency)}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label>Amount<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
                <AmountInput
                  amount={form.amount}
                  currency={form.currency}
                  onAmountChange={(amt) => setForm({ ...form, amount: amt })}
                  onCurrencyChange={(c) => setForm({ ...form, currency: c })}
                  extraCurrencies={[tenantCurrency]}
                  required
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Spent on<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
              <Input
                type="date"
                value={form.spent_at}
                onChange={(e) => setForm({ ...form, spent_at: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Lunch with client, mileage to airport, conference ticket…"
              />
            </div>
          </div>

          {/* Routing + reimbursement (new expenses only) */}
          {mode === 'new' && (
            <div className="mt-5 pt-5 border-t border-ink/8 dark:border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Approval routing</Label>
                <Select
                  value={form.approval_chain_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, approval_chain_id: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Single approval (default)</SelectItem>
                    {chains.map((ch) => (
                      <SelectItem key={ch.id} value={ch.id}>
                        {ch.name} ({ch.levels.length} level{ch.levels.length === 1 ? '' : 's'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[12px] text-ink/50 dark:text-white/50">
                  Multi-level chains require each approver to sign off in order.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Reimbursement</Label>
                <label className="flex items-center gap-2.5 h-10 px-3 rounded-xl bg-pearl-100/70 dark:bg-white/[0.05] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.reimbursable}
                    onChange={(e) => setForm((f) => ({ ...f, reimbursable: e.target.checked }))}
                    className="h-4 w-4 rounded accent-coral-600"
                  />
                  <span className="text-[13px] text-ink dark:text-white">
                    Reimburse to the employee via payroll once approved
                  </span>
                </label>
              </div>
            </div>
          )}
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <div className="mb-4">
            <h3 className={VISUAL.dashSection}>Receipts</h3>
            <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">
              Attach images or PDFs. Receipts upload after the expense is saved.
            </p>
          </div>
          <ReceiptUpload
            attachments={existing}
            pending={pending}
            onAdd={addFiles}
            onRemovePending={removePending}
            onRemoveAttachment={mode === 'edit' ? removeExistingAttachment : undefined}
            disabled={saving}
          />
        </section>

        <div className="flex items-center justify-between pt-2">
          <Link
            to={cancelHref}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back without saving
          </Link>
          <div className="flex items-center gap-2">
            {mode === 'new' && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={saving}
                className="gap-1.5"
                onClick={() => doSave(true)}
              >
                <FileText className="h-3.5 w-3.5" />
                Save as draft
              </Button>
            )}
            <Button type="submit" disabled={saving} size="lg" className="gap-1.5">
              <Save className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Submit expense'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
